import { useState, useEffect } from 'react';
import { fmtAmount, fmtDate, today } from '../lib/helpers';
import { api } from '../lib/api';

function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export function ReportsPage({ parties }) {
  const [start, setStart]   = useState(firstOfMonth());
  const [end, setEnd]       = useState(today());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab]       = useState('all');

  const fetch = async () => {
    setLoading(true);
    try { setReport(await api.getReport(start, end)); }
    catch (e) { alert(e.message); }
    setLoading(false);
  };

  useEffect(() => { fetch(); }, []);

  const partyName = (pid) => parties.find(p => p.id === pid)?.name || 'Unknown';

  const filtered = (report?.transactions || []).filter(t =>
    tab === 'all' ? true : t.type === tab
  );

  const printReport = () => {
    const lines = filtered.map(t =>
      `${fmtDate(t.date)}\t${t.party_name || partyName(t.party_id)}\t${t.type.toUpperCase()}\t${fmtAmount(t.amount)}\t${t.note || ''}`
    ).join('\n');
    const win = window.open('', '_blank');
    win.document.write(`<pre style="font-family:monospace;padding:20px">
CASHBOOK REPORT
Period: ${start} to ${end}
Total Credit: ${fmtAmount(report?.total_credit || 0)}
Total Debit:  ${fmtAmount(report?.total_debit  || 0)}
Net:          ${fmtAmount((report?.total_credit || 0) - (report?.total_debit || 0))}

DATE\t\tPARTY\t\t\tTYPE\t\tAMOUNT\t\tNOTE
${'─'.repeat(80)}
${lines}
</pre>`);
    win.print();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Date range filter */}
      <div style={{
        padding: '12px 16px', background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-mid)' }}>📅 From</span>
        <input className="form-input" type="date" value={start} onChange={e => setStart(e.target.value)}
          style={{ width: 'auto', flex: 1 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-mid)' }}>To</span>
        <input className="form-input" type="date" value={end} onChange={e => setEnd(e.target.value)}
          style={{ width: 'auto', flex: 1 }} />
        <button className="btn btn-blue btn-sm" onClick={fetch}>Apply</button>
        <button className="btn btn-sm" onClick={printReport}>🖨 Print</button>
      </div>

      {/* Totals */}
      {report && (
        <div className="report-total-bar">
          <div className="report-total-item">
            <div className="report-total-label">Total Credit 📥</div>
            <div className="report-total-amount" style={{ color: 'var(--green-dark)' }}>{fmtAmount(report.total_credit)}</div>
          </div>
          <div className="report-total-item">
            <div className="report-total-label">Total Debit 📤</div>
            <div className="report-total-amount" style={{ color: 'var(--red-dark)' }}>{fmtAmount(report.total_debit)}</div>
          </div>
          <div className="report-total-item">
            <div className="report-total-label">Net Balance</div>
            <div className="report-total-amount" style={{
              color: report.total_credit >= report.total_debit ? 'var(--green-dark)' : 'var(--red-dark)'
            }}>{fmtAmount(Math.abs(report.total_credit - report.total_debit))}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        {[
          { id: 'all',    label: 'All' },
          { id: 'credit', label: '📥 Credit' },
          { id: 'debit',  label: '📤 Debit' },
        ].map(t => (
          <button key={t.id} className={`tab-btn${tab === t.id ? ' active' : ''}`}
            onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* Table */}
      <div className="content">
        {loading ? (
          <div className="empty">
            <div className="loading-spin" style={{ margin: '0 auto 8px' }} />Loading…
          </div>
        ) : (
          <div style={{ padding: '12px 16px' }}>
            {filtered.length === 0 ? (
              <div className="empty">
                <div className="empty-icon">📊</div>
                No transactions in this period
              </div>
            ) : (
              <>
                {/* Summary by party */}
                <div className="section-title" style={{ marginBottom: 10 }}>Party-wise Summary</div>
                <div style={{ marginBottom: '1.25rem', background: 'var(--surface)', borderRadius: 12, overflow: 'hidden', boxShadow: 'var(--shadow)' }}>
                  {Object.entries(
                    filtered.reduce((acc, t) => {
                      const name = t.party_name || partyName(t.party_id);
                      if (!acc[name]) acc[name] = { credit: 0, debit: 0 };
                      acc[name][t.type] += t.amount;
                      return acc;
                    }, {})
                  ).map(([name, bal]) => (
                    <div key={name} style={{
                      display: 'flex', alignItems: 'center', padding: '10px 14px',
                      borderBottom: '1px solid var(--border)',
                    }}>
                      <span style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>{name}</span>
                      <span style={{ color: 'var(--green-dark)', fontWeight: 600, fontSize: 13, marginRight: 16 }}>
                        {bal.credit > 0 ? `+${fmtAmount(bal.credit)}` : ''}
                      </span>
                      <span style={{ color: 'var(--red-dark)', fontWeight: 600, fontSize: 13 }}>
                        {bal.debit > 0 ? `-${fmtAmount(bal.debit)}` : ''}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="section-title" style={{ marginBottom: 10 }}>All Entries ({filtered.length})</div>
                <div className="txn-list">
                  {filtered.map(t => (
                    <div key={t.id} className="txn-row">
                      <div className={`txn-dot ${t.type}`}>
                        {t.type === 'credit' ? '📥' : '📤'}
                      </div>
                      <div className="txn-info">
                        <div className="txn-note">{t.note || (t.type === 'credit' ? 'Credit entry' : 'Debit entry')}</div>
                        <div className="txn-party">👤 {t.party_name || partyName(t.party_id)}</div>
                        <div className="txn-date">{fmtDate(t.date)}</div>
                      </div>
                      <div className="txn-right">
                        <div className={`txn-amount ${t.type}`}>{fmtAmount(t.amount)}</div>
                        <div className={`txn-label ${t.type}`}>{t.type === 'credit' ? 'Credit' : 'Debit'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
